import { Flex } from '@mantine/core';
import classNames from 'classnames';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { BannersnackCreative, Creative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import {
  ActionBar,
  Button,
  CardV2 as Card,
  ContextMenu,
  SaveButtonValid,
  Tab,
  Toolbar,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import BannersnackMetadata from '@feathr/extender/components/BannersnackMetadata';
import CreativeMetadata from '@feathr/extender/components/CreativeMetadata';
import CreativeThumbnail from '@feathr/extender/components/CreativeThumbnail';
import { useClearUnsavedChanges } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { useRedirect } from '@feathr/hooks';

import DownloadBannersnackFileMenuItem from '../../DownloadBannersnackFileMenuItem';
import CreativeEditor from './CreativeEditor';

import * as styles from './EventCreativeEditPage.css';

interface IDownloadMenuProps {
  creative: BannersnackCreative;
}

function DownloadMenu({ creative }: IDownloadMenuProps): JSX.Element {
  const animated = CreativeClass.DisplayBannersnack ? creative.get('animated') : false;

  return (
    <ContextMenu iconName={'ellipsis'}>
      <DownloadBannersnackFileMenuItem creative={creative} type={'jpg'} />
      <DownloadBannersnackFileMenuItem creative={creative} type={'png'} />
      <DownloadBannersnackFileMenuItem creative={creative} type={'pdf'} />
      {animated && (
        <>
          <DownloadBannersnackFileMenuItem creative={creative} type={'animated-gif'} />
          <DownloadBannersnackFileMenuItem creative={creative} type={'mp4'} />
        </>
      )}
    </ContextMenu>
  );
}

function EventCreativeEditPage(): JSX.Element {
  const { t } = useTranslation();
  const { Creatives } = useStore();
  const localUrl = useLocalUrl();
  const [redirect, setRedirect, hasRedirect] = useRedirect();
  const { creativeId, eventId, tab } = useParams<{
    creativeId: string;
    eventId: string;
    tab: string;
  }>();

  const creative = Creatives.get(creativeId) as DisplayCreative;
  const type = creative.get('_cls');
  const showBackButton = hasRedirect;

  const orientation: 'horizontal' | 'vertical' =
    creative.get('width') <= creative.get('height') ? 'vertical' : 'horizontal';

  useClearUnsavedChanges({
    model: creative,
  });

  function handleSave(): void {
    redirect(localUrl(`/projects/${eventId}/creatives`));
  }

  const backButton = <Button onClick={handleSave}>{t('Return to campaign')}</Button>;
  const actions =
    showBackButton || type === CreativeClass.DisplayBannersnack ? (
      <Toolbar>
        {showBackButton && backButton}
        {type === CreativeClass.DisplayBannersnack && (
          <DownloadMenu creative={creative as BannersnackCreative} />
        )}
      </Toolbar>
    ) : undefined;

  const validationFields: string[] = ['alt_text', 'destination_url', 'name'];
  if (type === CreativeClass.DisplayAdTag) {
    validationFields.push('adtag', 'spec');
  }
  if (type === CreativeClass.DisplayBannersnack) {
    validationFields.push('banner_hash');
  }

  const validationErrors = creative.validate(validationFields, false);
  const title = t('Edit {{name}}', { name: creative.name });

  const tabs =
    type === CreativeClass.DisplayBannersnack
      ? [
          <Tab
            key={'design'}
            // Keep including redirect in url, but only if it's already set.
            link={hasRedirect ? setRedirect('design', undefined, true) : 'design'}
            title={t('Design')}
          />,
          <Tab
            key={'metadata'}
            // Keep including redirect in url, but only if it's already set.
            link={hasRedirect ? setRedirect('metadata', undefined, true) : 'metadata'}
            title={t('Metadata')}
          />,
        ]
      : [];
  // TODO: This would make a great modal
  // TODO: Resize images down the way we do in in add image/video
  return (
    <Page actions={actions} loading={creative.isPending} tabs={tabs} title={title}>
      {type !== CreativeClass.DisplayBannersnack && (
        <Card>
          <Card.Header title={title} />
          <Card.Content addVerticalGap={true}>
            {creative.get('url') && (
              <Flex align={'center'}>
                <CreativeThumbnail
                  className={classNames(styles[orientation as keyof typeof styles])}
                  creative={creative.toJS()}
                  height={orientation === 'horizontal' ? 400 : undefined}
                  width={orientation === 'vertical' ? 200 : 'auto'}
                />
                <dl>
                  <dt>{t('Dimensions')}</dt>
                  <dd>{creative.dimensions}</dd>
                  {creative.getSpec() && (
                    <>
                      <dt>{t('Spec')}</dt>
                      <dd>{creative.getSpec()?.name}</dd>
                    </>
                  )}
                  <dt>{t('Aspect Ratio')}</dt>
                  <dd>{creative.aspectRatio}</dd>
                </dl>
              </Flex>
            )}
            <CreativeMetadata
              creative={creative}
              showInlineEditor={true}
              showName={false}
              showReplaceButton={true}
            />
          </Card.Content>
        </Card>
      )}
      {type === CreativeClass.DisplayBannersnack && (
        <section className={styles.content}>
          {!tab || (tab === 'design' && <CreativeEditor creative={creative} />)}
          {tab === 'metadata' && <BannersnackMetadata creative={creative} />}
        </section>
      )}
      <ActionBar
        left={showBackButton && backButton}
        right={
          <>
            {type !== CreativeClass.DisplayBannersnack && (
              <SaveButtonValid<Creative>
                disabled={!creative.isDirty}
                errors={toJS(validationErrors.errors)}
                method={'patch'}
                model={creative}
                onSave={handleSave}
                type={'primary'}
              />
            )}
          </>
        }
        usePortal={true}
      />
    </Page>
  );
}

export default observer(EventCreativeEditPage);
